import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import apiService from '../services/apiService';
import './LanguagesPage.css';

const LANG_OPTIONS = [
  { value: 'gr', label: 'Ελληνικά (GR)' },
  { value: 'en', label: 'English (EN)' },
  { value: 'de', label: 'Deutsch (DE)' },
];

/**
 * Languages page
 * Language selection via radio button, then display + editing of the corresponding JSON file.
 */
function LanguagesPage() {
  const { t } = useLanguage();

  const [selectedLang, setSelectedLang] = useState('de');
  const [text, setText] = useState('');
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load content when language changes
  useEffect(() => {
    loadLanguage(selectedLang);
    setIsEditing(false);
    setError(null);
  }, [selectedLang]);

  const loadLanguage = async (lang) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLanguage(lang);
      setText(data.text || '{}');
    } catch (err) {
      setError(err.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLangChange = (lang) => {
    setSelectedLang(lang);
  };

  const handleEditClick = () => {
    setEditText(text);
    setIsEditing(true);
    setError(null);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSaveClick = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiService.saveLanguage(selectedLang, editText);
      setText(editText);
      setIsEditing(false);
      showSuccess(t('languagesPage.savedSuccess'));
    } catch (err) {
      setError(err.message || t('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  return (
    <div className="info-page">
      <div className="info-header">
        <h2>{t('languagesPage.title')}</h2>
      </div>

      {/* Radio selection */}
      <div className="lang-radio-group">
        {LANG_OPTIONS.map((opt) => (
          <label key={opt.value} className={`lang-radio-label${selectedLang === opt.value ? ' lang-radio-label--active' : ''}`}>
            <input
              type="radio"
              name="language"
              value={opt.value}
              checked={selectedLang === opt.value}
              onChange={() => handleLangChange(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {error && (
        <div className="error-message">
          <strong>{t('messages.error')}</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <strong>{t('messages.success')}</strong> {successMessage}
        </div>
      )}

      {loading ? (
        <div className="loading">{t('messages.loading')}</div>
      ) : isEditing ? (
        <div className="info-edit">
          <textarea
            className="info-textarea lang-textarea"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={20}
            spellCheck={false}
            autoFocus
          />
          <div className="info-edit-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveClick}
              disabled={saving}
            >
              {saving ? t('info.saving') : t('buttons.save')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancelClick}
              disabled={saving}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="info-edit">
          <div className="info-header" style={{ marginBottom: '10px' }}>
            <span className="lang-file-label">
              languages/{selectedLang}.json
            </span>
            <button className="btn btn-primary" onClick={handleEditClick}>
              {t('buttons.edit')}
            </button>
          </div>
          <pre className="lang-json-preview">{text}</pre>
        </div>
      )}
    </div>
  );
}

export default LanguagesPage;
